import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FiChevronDown, FiLogOut, FiSettings } from 'react-icons/fi';
import { useAuth, readCachedIsStaff } from '../../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MANUFACTURER_REQUESTS_LAUNCHED } from '../../config/featureFlags';
import { useConfirm } from '../ConfirmDialog';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function initialsFor(name, email) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length) return `${words[0][0]}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase();
  return String(email || '?').charAt(0).toUpperCase();
}

/**
 * Who's signed in, at the far right: the way to profile settings (password,
 * team, billing) and out.
 */
function ProfileMenu({ active, onNavigate, onLogout }) {
  const { supplierInfo, user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = supplierInfo?.full_name || '';
  const email = supplierInfo?.email || user?.email || '';
  const org = supplierInfo?.organisation_name || '';
  const close = () => { setOpen(false); onNavigate(); };

  return (
    <div className="nav-profile" ref={ref}>
      <button
        type="button"
        className={`nav-profile__toggle ${active ? 'is-active' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="nav-profile__avatar" aria-hidden="true">{initialsFor(name, email)}</span>
        <span className="nav-profile__name">{name || email || 'Account'}</span>
        <FiChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div className="nav-profile__menu" role="menu">
          <div className="nav-profile__who">
            <div className="nav-profile__who-name">{name || email}</div>
            {name && email && <div className="nav-profile__who-line">{email}</div>}
            {org && <div className="nav-profile__who-line">{org}</div>}
          </div>
          <Link role="menuitem" className="nav-profile__item" to="/profile" onClick={close}>
            <FiSettings size={14} aria-hidden="true" />Profile settings
          </Link>
          <button role="menuitem" type="button" className="nav-profile__item" onClick={() => { close(); onLogout(); }}>
            <FiLogOut size={14} aria-hidden="true" />Log out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [pcfRequestCount, setPcfRequestCount] = useState(0);
  const { signOut, isStaff: liveIsStaff, supplierInfo, getToken } = useAuth();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const { pathname } = useLocation();

  // Prefer the live value once supplierInfo has loaded; before then, use the
  // cached hint so a staff user doesn't see a layout shift.
  const isStaff = supplierInfo ? liveIsStaff : readCachedIsStaff();

  useEffect(() => {
    if (supplierInfo?.account_type !== 'Supplier') return;
    let cancelled = false;
    getToken().then(token => {
      if (!token || cancelled) return;
      axios.get(`${API_BASE_URL}/supplier/pcf-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => {
        if (!cancelled) setPcfRequestCount(res.data?.count || 0);
      }).catch(() => {});
    });
    return () => { cancelled = true; };
  }, [supplierInfo, getToken]);

  const handleLogout = async () => {
    const ok = await confirm({
      title: 'Log out of Zilch?',
      message: 'You will need to sign in again to get back into Zilch.',
      confirmLabel: 'Log out',
      cancelLabel: 'Stay signed in',
    });
    if (!ok) return;
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if there's an error, redirect to login
      navigate('/login');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const showManufacturers = (MANUFACTURER_REQUESTS_LAUNCHED && ['Supplier', 'Enterprise'].includes(supplierInfo?.account_type)) || isStaff;

  // Pages, in order. `match` decides the active item.
  const items = [
    { to: '/', label: 'Home', match: (p) => p === '/' },
    { to: '/products', label: 'Products', match: (p) => p === '/products', dot: pcfRequestCount > 0 },
    showManufacturers && { to: '/manufacturers', label: 'Manufacturers', match: (p) => p.startsWith('/manufacturers') },
    { to: '/compliance', label: 'Compliance', match: (p) => p.startsWith('/compliance') },
    { to: '/reports', label: 'Reports', match: (p) => p.startsWith('/reports') },
    isStaff && { to: '/admin', label: 'Admin', match: (p) => p.startsWith('/admin') },
  ].filter(Boolean);

  return (
    <nav className="navbar navbar-expand-sm modern-navbar">
      <Link className="navbar-brand d-flex align-items-center gap-2" to="/">
        <img
          src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
          width="35"
          height="35"
          className="d-inline-block align-top"
          alt="Zilch Logo"
        />
        Zilch.
      </Link>
      <button
        className="navbar-toggler"
        type="button"
        onClick={toggleMenu}
        aria-controls="navbarSupportedContent"
        aria-expanded={isMenuOpen}
        aria-label="Toggle navigation"
      >
        <span className="navbar-toggler-icon"></span>
      </button>

      <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`} id="navbarSupportedContent">
        <ul className="navbar-nav me-auto modern-navbar__pages">
          {items.map(({ to, label, match, dot }) => (
            <li key={to} className={`nav-item ${match(pathname) ? 'active' : ''}`}>
              <Link
                className="nav-link"
                to={to}
                onClick={closeMenu}
                style={dot ? { position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 } : undefined}
              >
                <span>{label}</span>
                {dot && <span className="notification-dot" />}
              </Link>
            </li>
          ))}
        </ul>
        <ProfileMenu
          active={pathname === '/profile' || pathname.startsWith('/billing')}
          onNavigate={closeMenu}
          onLogout={handleLogout}
        />
      </div>
    </nav>
  );
}
